import React, { useState, useEffect, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { ajaxGetPoTerms, ajaxSavePoTerm, ajaxDeletePoTerm } from '../../services/poTermService';
import inMemoryUser from '../../services/inMemoryUser';
import { noty } from '../../helpers';
import ConfirmationButton from '../../components/ConfirmationButton';

export default function PoTermDialog({ open, onClose, onSave }) {
  const [terms, setTerms] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTerm, setEditingTerm] = useState(null);
  const [termName, setTermName] = useState('');
  const [showPrompt, setShowPrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [lastModifiedId, setLastModifiedId] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) {
      loadTerms();
    }
  }, [open]);

  const loadTerms = () => {
    setIsLoading(true);
    ajaxGetPoTerms()
      .then((res) => {
        setTerms(res.data || []);
      })
      .catch((error) => {
        console.error('Error loading terms:', error);
        noty('Failed to load terms', 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredTerms = terms.filter(term =>
    term.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddClick = () => {
    setEditingTerm(null);
    setTermName('');
    setShowError(false);
    setShowPrompt(true);
  };

  const handleEditClick = (term) => {
    setEditingTerm(term);
    setTermName(term.name);
    setShowError(false);
    setShowPrompt(true);
  };

  const handleDeleteClick = (term) => {
    setIsLoading(true);
    ajaxDeletePoTerm(term.id)
      .then(() => {
        loadTerms();
        onSave();
      })
      .catch((error) => {
        console.error('Error deleting term:', error);
        noty('Failed to delete term', 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePromptSubmit = () => {
    if (!termName.trim()) {
      setShowError(true);
      return;
    }

    setIsLoading(true);
    const data = editingTerm ? {
      id: editingTerm.id,
      name: termName.trim()
    } : {
      name: termName.trim()
    };

    ajaxSavePoTerm(data)
      .then((res) => {
        loadTerms();
        setShowPrompt(false);
        onSave();
        setLastModifiedId(editingTerm ? editingTerm.id : res.data.id);
      })
      .catch((error) => {
        console.error('Error saving term:', error);
        noty('Failed to save term', 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePromptCancel = () => {
    setShowPrompt(false);
    setEditingTerm(null);
    setTermName('');
    setShowError(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handlePromptSubmit();
    }
  };

  useEffect(() => {
    if (lastModifiedId && listRef.current) {
      const element = listRef.current.querySelector(`[data-id="${lastModifiedId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [lastModifiedId, terms]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: '700px',
            maxWidth: '700px'
          }
        }}
      >
        <DialogTitle>Manage Terms</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search terms..."
                  value={searchQuery}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddClick}
                  disabled={!inMemoryUser.isAdmin()}
                  sx={{ ml: 2 }}
                >
                  Add
                </Button>
              </div>
            </Grid>
            <Grid item xs={12}>
              <List 
                ref={listRef}
                sx={{ 
                  minHeight: '500px',
                  maxHeight: '500px',
                  overflow: 'auto',
                  '& .MuiListItem-root': {
                    py: 1,
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }
                }}
              >
                {filteredTerms.map((term) => (
                  <ListItem
                    key={term.id}
                    data-id={term.id}
                  >
                    <ListItemText primary={term.name} />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={!inMemoryUser.isAdmin()}
                        onClick={() => handleEditClick(term)}
                        sx={{ minWidth: '36px', mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <ConfirmationButton
                        buttonProps={{
                          size: "small",
                          color: "error",
                          disabled: !inMemoryUser.isAdmin(),
                          sx: { minWidth: '36px' }
                        }}
                        handleDialogClickYes={() => handleDeleteClick(term)}
                        buttonText={<DeleteIcon />}
                        dialogText={`Are you sure you want to delete "${term.name}"?`}
                        dialogYesText="Confirm"
                        dialogNoText="Cancel"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showPrompt}
        onClose={handlePromptCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {editingTerm ? 'Edit Term' : 'Add Term'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Term Name"
            type="text"
            fullWidth
            value={termName}
            onChange={(e) => {
              setTermName(e.target.value);
              setShowError(false);
            }}
            onKeyDown={handleKeyDown}
            error={showError && !termName.trim()}
            helperText={showError && !termName.trim() ? 'Term name is required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePromptCancel}>Cancel</Button>
          <Button onClick={handlePromptSubmit} variant="contained" disabled={isLoading}>
            {editingTerm ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
